import { Spacing } from '@persona-id/design-tokens';
import { ChevronDownIcon } from '@persona-id/design-tokens/icons';
import { ReactNode, SyntheticEvent, useCallback } from 'react';
import { COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { headingMixins, paragraphMixins } from 'design-system/mixins/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';
import { assertNever } from 'lib/utils/types';

export const TitleContainer = styled.div<{
  disabled?: boolean;
  disabledTheme?: 'disabled' | 'primary';
  menuItem?: boolean;
  caretSize?: 'lg' | 'md' | 'sm';
  padded?: boolean;
  menuItemBorder?: boolean;
  menuItemHover?: boolean;
  titleContainerHeight?: string;
  alignCaret?: 'start' | 'end' | 'center' | 'stretch';
  rounded?: boolean;
  titleGapSize?: string;
}>`
  cursor: pointer;
  display: grid;
  align-items: ${({ alignCaret }) => alignCaret};
  grid-column-gap: ${({ titleGapSize }) => (titleGapSize ? titleGapSize : `${Spacing[4]}`)};
  grid-template-columns: ${({ menuItem }) => (menuItem ? '1fr 24px' : '24px 1fr')};
  ${({ menuItem, caretSize }) => {
    if (menuItem || caretSize === 'lg') return paragraphMixins.lgBold;
    else if (caretSize === 'md') {
      return paragraphMixins.lgBold;
    } else if (caretSize === 'sm') {
      return paragraphMixins.mdBold;
    } else {
      return headingMixins.sm;
    }
  }};

  height: ${({ theme, titleContainerHeight }) =>
    titleContainerHeight ? titleContainerHeight : theme.element.height.md};
  border-bottom: ${({ theme, menuItem, menuItemBorder }) =>
    menuItem && menuItemBorder ? `1px solid ${theme.color.border.secondary}` : null};
  border-radius: ${({ rounded, theme }) => (rounded ? `${theme.element.borderRadius}` : 0)};

  ${({ disabled, theme }) =>
    disabled &&
    css`
      cursor: default;
      color: ${theme.color.text.disabled};
    `}

  ${({ disabled, disabledTheme, menuItem, theme, padded }) =>
    menuItem &&
    css`
      transition-duration: ${TransitionDuration.Fast};
      padding-left: ${padded ? Spacing[5] : Spacing[0]};
      padding-right: ${padded ? Spacing[4] : Spacing[0]};
      color: ${disabled && disabledTheme === 'disabled'
        ? theme.color.text.disabled
        : theme.color.text.primary};
    `}

  ${({ disabled, menuItem, menuItemHover, theme }) =>
    menuItem &&
    menuItemHover &&
    css`
      &:hover {
        background-color: ${disabled ? 'unset' : theme.color.background.highlight};
      }
    `}
`;

const StyledIcon = styled(
  ({ disabled, expanded, menuItem, alignCaret, justifyCaret, ...props }) => (
    <ChevronDownIcon {...props} />
  ),
)<{
  disabled?: boolean;
  expanded: boolean;
  menuItem?: boolean;
  justifyCaret?: 'start' | 'end' | 'center' | 'stretch';
  alignCaret?: 'start' | 'end' | 'center' | 'stretch';
}>`
  transition: transform ${TransitionDuration.Fast};
  transform: rotate(
    ${({ expanded, menuItem }) => (expanded ? '0' : menuItem ? '-180deg' : '-90deg')}
  );
  justify-self: ${({ justifyCaret }) => justifyCaret ?? 'center'};

  margin-top: ${({ alignCaret }) => (alignCaret === 'start' ? `${Spacing[2]}` : '0px')};

  rect[fill],
  path[fill] {
    fill: ${({ theme, disabled }) =>
      disabled ? theme.color.text.disabled : theme.color.text.primary};
  }
`;

const AccordionContainer = styled('div')<{
  borderBottom?: boolean;
  rounded: boolean;
  showCaretOnHover?: boolean;
}>`
  border-bottom: 1px solid ${({ borderBottom }) => (borderBottom ? COLORS.gray20 : 'unset')};
  border-radius: ${({ rounded }) => (rounded ? '5px' : 0)};

  ${({ showCaretOnHover }) =>
    showCaretOnHover &&
    css`
      & ${StyledIcon} {
        opacity: 0;
        transition: opacity ${TransitionDuration.Fast} ease;
      }
      &:hover {
        & ${StyledIcon} {
          opacity: 1;
        }
      }
    `}
`;
interface AccordionProps {
  children?: ReactNode;
  className?: string;
  count?: number;
  dataTest?: string;
  defaultExpanded?: boolean;
  overrideExpanded?: boolean | null;
  onTitleClick?: (clickEvent: SyntheticEvent<HTMLDivElement>) => void;
  onChange?: (expanded: boolean) => void;
  disabled?: boolean;
  disabledTheme?: 'disabled' | 'primary';
  disabledHideCaret?: boolean;
  showCaret?: boolean;
  menuItem?: boolean;
  caretSize?: 'lg' | 'md' | 'sm';
  borderBottom?: boolean;
  title: ReactNode;
  expandedTitle?: ReactNode;
  padded?: boolean;
  menuItemBorder?: boolean;
  titleContainerHeight?: string;
  menuItemHover?: boolean;
  alignCaret?: 'start' | 'end' | 'center' | 'stretch';
  justifyCaret?: 'start' | 'end' | 'center' | 'stretch';
  rounded?: boolean;
  titleGapSize?: string;
  showCaretOnHover?: boolean;
}

const Accordion = ({
  children,
  className,
  count,
  dataTest,
  defaultExpanded = true,
  overrideExpanded = null,
  onTitleClick,
  onChange,
  disabled,
  disabledTheme = 'disabled',
  disabledHideCaret,
  showCaret = true,
  menuItem,
  caretSize = 'md',
  borderBottom = false,
  padded = true,
  menuItemBorder = true,
  titleContainerHeight = 'auto',
  menuItemHover = true,
  title,
  expandedTitle,
  alignCaret = 'center',
  justifyCaret = 'center',
  rounded = false,
  titleGapSize,
  showCaretOnHover = false,
}: AccordionProps) => {
  const [expanded, { toggle }] = useToggle(defaultExpanded);

  let caretPixelSize;
  switch (caretSize) {
    case 'lg':
      // Currently not in use. Feel free to change this if needed.
      caretPixelSize = '20px';
      break;
    case 'md':
      caretPixelSize = '14px';
      break;
    case 'sm':
      caretPixelSize = '8px';
      break;
    default:
      assertNever(caretSize);
  }

  const resultExpanded = overrideExpanded != null ? overrideExpanded : expanded;

  const caret = (
    <StyledIcon
      height={caretPixelSize}
      width={caretPixelSize}
      disabled={disabled}
      expanded={resultExpanded}
      alignCaret={alignCaret}
      justifyCaret={justifyCaret}
      menuItem={menuItem}
    />
  );

  const handleClick = useCallback(
    (event: SyntheticEvent<HTMLDivElement>) => {
      if (disabled) {
        return undefined;
      }

      // Used if the expansion is managed by the parent
      if (onTitleClick) onTitleClick(event);
      if (onChange) onChange(!expanded);

      return toggle();
    },
    [disabled, toggle],
  );

  const hideCaret = (disabledHideCaret && disabled) || !showCaret;

  return (
    <AccordionContainer
      aria-expanded={resultExpanded ? 'true' : 'false'}
      className={className}
      borderBottom={borderBottom}
      rounded={rounded}
      showCaretOnHover={showCaretOnHover}
    >
      <TitleContainer
        role='button'
        disabled={disabled}
        disabledTheme={disabledTheme}
        id={dataTest}
        menuItem={menuItem}
        caretSize={caretSize}
        onClick={handleClick}
        padded={padded}
        menuItemBorder={menuItemBorder}
        menuItemHover={menuItemHover}
        titleContainerHeight={titleContainerHeight}
        alignCaret={alignCaret}
        data-test={dataTest}
        rounded={rounded}
        titleGapSize={titleGapSize}
      >
        {!menuItem && (hideCaret ? <div /> : caret)}
        <div>
          {resultExpanded && expandedTitle ? expandedTitle : title}
          {count != null && ` (${count})`}
        </div>
        {menuItem && !hideCaret && caret}
      </TitleContainer>

      {resultExpanded && children}
    </AccordionContainer>
  );
};

export default Accordion;
